/**
 * 🚪 Onboarding-state resolver — cache + retry + positive-signal cascade
 *
 * Shared between `web/src/app/index.tsx` (native cold-launch) and
 * `web/src/pages/Splash.tsx` (web). One implementation, one place to
 * update.
 *
 * WHY THIS EXISTS
 * ───────────────
 * On 2026-05-20 three established users (Matt 34d, Kirsten 19d,
 * Paul 14d — all `onboarding_completed=true`, `onboarding_step=8`) saw
 * the "From the Coach Cedar Team" welcome letter on cold launch and
 * had to click Continue to dismiss it, generating fresh
 * `welcome_letter` rows in `user_agreement_log`. Their DB state said
 * they should route straight to chat — they couldn't, because the
 * ALB returned 5xx for their `/api/users/onboarding-state` requests
 * (HTTPCode_ELB_5XX_Count peaked at 178 in one 10-min bucket; API
 * target 5xx was 1 over the same 12 hours — the storm was entirely
 * at the load-balancer layer). The pre-fix routing default on
 * `!stateRes.ok` was `/(auth)/onboarding`, with a local-flag fallback
 * that these users didn't have (the flag was added to Onboarding.tsx
 * after they completed onboarding).
 *
 * THE CONTRACT
 * ────────────
 * 1. Cache every successful response under
 *    `${userId}_onboarding_state_cache` (30-day TTL). Cold-launches
 *    during backend instability fall back to this snapshot — far more
 *    accurate than any heuristic.
 *
 * 2. Retry ONCE on 5xx with jittered 500–1000ms backoff. Without
 *    jitter, simultaneous cold-launches during an ALB blip synchronize
 *    their retries and amplify the storm. 4xx does NOT retry — 401 is
 *    a token issue (re-auth), retrying with the same dead token is
 *    pointless.
 *
 * 3. Positive-signal cascade for the fallback path. After retry
 *    exhaustion, check positive evidence of established use in order:
 *      a. Prior cached state present (within TTL).
 *      b. `cedar_onboarding_seen` flag set to 'true'.
 *      c. Local chat-message store has content.
 *    The ABSENCE of (b) or (c) is never used as evidence the user is
 *    new — only the absence of ALL three together routes to onboarding.
 *
 * 4. 401 short-circuits to `{kind: 'unauthorized'}` regardless of
 *    cached/local signals. Caller must re-auth.
 *
 * BACKEND CONTRACT THIS DEPENDS ON
 * ────────────────────────────────
 * `/api/users/onboarding-state` must raise HTTPException(5xx) on
 * internal errors — never return HTTP 200 with zeroed flags. See
 * `routers/user_data.py` + `tests/gate/test_onboarding_state_contract.py`.
 *
 * Locked in by `web/src/__tests__/entryRoutingFallback.test.ts`.
 */

import { storage } from './storage';

// ───────────────────────────────────────────────────────────────────────
// Tunable constants — keep these at the top so a future operator can
// adjust without spelunking. All time values in milliseconds.
// ───────────────────────────────────────────────────────────────────────

/** Suffix appended to userId for the cache key. */
export const CACHE_KEY_SUFFIX = '_onboarding_state_cache';

/** Suffix appended to userId for the local chat-message store key. */
export const MESSAGES_KEY_SUFFIX = '_chat_messages';

/** How long a cached state is considered fresh enough to use as fallback.
 *  Onboarding state is monotonic (once completed, stays completed) so a
 *  stale "completed" cache is always safe. 30 days is conservative. */
export const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Jittered retry-delay floor on 5xx. */
export const RETRY_DELAY_MIN_MS = 500;

/** Jittered retry-delay ceiling on 5xx. */
export const RETRY_DELAY_MAX_MS = 1000;

// ───────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────

export interface OnboardingState {
  onboarding_step: number;
  onboarding_completed: boolean;
  tour_completed: boolean;
  name_collection_completed: boolean;
  has_names: boolean;
  has_accepted_legal: boolean;
  has_accepted_health_disclaimer: boolean;
  has_seen_welcome_letter: boolean;
  personality_slider_state: string | null;
  personality_sliders: unknown;
}

/** Discriminated result of a resolve attempt.
 *
 *   - `fresh`           — just fetched from the backend. Authoritative.
 *   - `cached`          — prior successful fetch, within TTL. Treat as
 *                         authoritative (state is monotonic).
 *   - `positive_signal` — no state to act on, but the user has clear
 *                         evidence of established use (flag or local
 *                         messages). Caller should route to chat.
 *   - `unauthorized`    — 401 from the backend. Caller must re-auth.
 *   - `no_signal`       — no fetch success, no cache, no flag, no
 *                         messages. Truly indistinguishable from a new
 *                         user. Caller routes to onboarding.
 */
export type ResolveResult =
  | { kind: 'fresh'; state: OnboardingState }
  | { kind: 'cached'; state: OnboardingState; ageMs: number }
  | { kind: 'positive_signal'; signal: 'cedar_onboarding_seen' | 'has_messages' }
  | { kind: 'unauthorized' }
  | { kind: 'no_signal' };

export interface ResolveOptions {
  /** Abort signal — AbortError propagates. */
  signal?: AbortSignal;
  /** Override the message-store key. Defaults to `${userId}_chat_messages`. */
  messageStoreKey?: string;
}

interface CachedPayload {
  state: OnboardingState;
  fetched_at: number;
}

// ───────────────────────────────────────────────────────────────────────
// Internal helpers
// ───────────────────────────────────────────────────────────────────────

function cacheKey(userId: string): string {
  return `${userId}${CACHE_KEY_SUFFIX}`;
}

function jitteredDelay(): number {
  // Math.random() ∈ [0, 1)  →  delay ∈ [MIN, MAX)
  return RETRY_DELAY_MIN_MS + Math.random() * (RETRY_DELAY_MAX_MS - RETRY_DELAY_MIN_MS);
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = () => {
        clearTimeout(timer);
        const e = new Error('AbortError');
        e.name = 'AbortError';
        reject(e);
      };
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

async function writeCache(userId: string, state: OnboardingState): Promise<void> {
  try {
    const payload: CachedPayload = { state, fetched_at: Date.now() };
    await storage.setItem(cacheKey(userId), JSON.stringify(payload));
  } catch (err) {
    // Cache write is best-effort. Failure here doesn't affect routing —
    // the caller already has `state` from the live fetch. Log and move on.
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.warn('[onboardingStateResolver] cache write failed:', err);
    }
  }
}

async function readCache(
  userId: string,
): Promise<{ state: OnboardingState; ageMs: number } | null> {
  try {
    const raw = await storage.getItem(cacheKey(userId));
    if (!raw) return null;
    const parsed: CachedPayload = JSON.parse(raw);
    if (!parsed?.state || typeof parsed.fetched_at !== 'number') return null;
    const ageMs = Date.now() - parsed.fetched_at;
    // Reject negative ages (clock skew) and stale entries beyond TTL.
    if (ageMs < 0 || ageMs > CACHE_TTL_MS) return null;
    return { state: parsed.state, ageMs };
  } catch {
    // Bad JSON, missing fields, storage error — treat as no cache.
    return null;
  }
}

async function hasLocalMessages(messagesKey: string): Promise<boolean> {
  try {
    const raw = await storage.getItem(messagesKey);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

async function fetchOnce(
  apiBaseUrl: string,
  jwt: string,
  signal?: AbortSignal,
): Promise<Response> {
  return fetch(`${apiBaseUrl}/api/users/onboarding-state`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    signal,
  });
}

// ───────────────────────────────────────────────────────────────────────
// Public API
// ───────────────────────────────────────────────────────────────────────

/**
 * Resolve user onboarding state with cache + retry + positive-signal
 * cascade. See file header for the full contract.
 *
 * Throws AbortError if the abort signal fires. Other network errors
 * (fetch throws, JSON parse fails on the success path) fall through to
 * the cascade — they are not exposed to the caller.
 */
export async function resolveOnboardingState(
  apiBaseUrl: string,
  jwt: string,
  userId: string,
  options?: ResolveOptions,
): Promise<ResolveResult> {
  const signal = options?.signal;
  const messageStoreKey =
    options?.messageStoreKey ?? `${userId}${MESSAGES_KEY_SUFFIX}`;

  // ─────────────────────────────────────────────────────────────────
  // Step 1: Fetch the live state. One retry on 5xx with jittered
  // backoff. 4xx does NOT retry (token issues, etc.).
  // ─────────────────────────────────────────────────────────────────
  let response: Response | null = null;
  try {
    response = await fetchOnce(apiBaseUrl, jwt, signal);
    if (response.status >= 500) {
      // Jittered backoff prevents synchronized retries amplifying an
      // ALB storm. Math.random() spreads cold-launches across the
      // 500-1000ms window.
      await sleep(jitteredDelay(), signal);
      response = await fetchOnce(apiBaseUrl, jwt, signal);
    }
  } catch (err) {
    // AbortError must propagate so the caller's overall timeout works.
    if ((err as Error)?.name === 'AbortError') throw err;
    response = null; // Network error — fall through to cascade
  }

  if (response) {
    if (response.ok) {
      try {
        const state = (await response.json()) as OnboardingState;
        await writeCache(userId, state);
        return { kind: 'fresh', state };
      } catch (err) {
        // JSON parse failed — treat the same as a network error and
        // fall through to the cascade.
        if (typeof __DEV__ !== 'undefined' && __DEV__) {
          console.warn('[onboardingStateResolver] response parse failed:', err);
        }
      }
    } else if (response.status === 401) {
      // 401 is unambiguous — the token is rejected. Don't paper over
      // it with a cached-state fallback; the caller must re-auth.
      return { kind: 'unauthorized' };
    }
    // 4xx other than 401, or 5xx after retry — fall through.
  }

  // ─────────────────────────────────────────────────────────────────
  // Step 2: Cached state from a prior successful fetch. Onboarding
  // state is monotonic, so a stale "completed" cache is always safe.
  // ─────────────────────────────────────────────────────────────────
  const cached = await readCache(userId);
  if (cached) {
    return { kind: 'cached', state: cached.state, ageMs: cached.ageMs };
  }

  // ─────────────────────────────────────────────────────────────────
  // Step 3: cedar_onboarding_seen positive-signal flag. Set when a
  // user clicked Continue on the welcome letter — confirms they got
  // past step 0.
  // ─────────────────────────────────────────────────────────────────
  const perUserFlag = await storage.getItem(`${userId}_cedar_onboarding_seen`);
  const globalFallbackFlag = await storage.getItem('cedar_onboarding_seen_fallback');
  if (perUserFlag === 'true' || globalFallbackFlag === 'true') {
    return { kind: 'positive_signal', signal: 'cedar_onboarding_seen' };
  }

  // ─────────────────────────────────────────────────────────────────
  // Step 4: Local chat-message store. Any cached message → user is
  // established. (Default key is `${userId}_chat_messages` — the
  // shape persistChatMessages() writes in useChatLogic.ts.)
  // ─────────────────────────────────────────────────────────────────
  if (await hasLocalMessages(messageStoreKey)) {
    return { kind: 'positive_signal', signal: 'has_messages' };
  }

  // ─────────────────────────────────────────────────────────────────
  // Step 5: No fetch success, no cache, no flag, no messages.
  // Genuinely indistinguishable from a brand-new user. Caller should
  // route to onboarding.
  // ─────────────────────────────────────────────────────────────────
  return { kind: 'no_signal' };
}

// ───────────────────────────────────────────────────────────────────────
// Local-only "established user?" peek (Cold-Start Latency Fix, 2026-05-25)
// ───────────────────────────────────────────────────────────────────────

/**
 * Result of a local-only established-user check. Mirrors the
 * positive-evidence tiers of resolveOnboardingState (cache → flag →
 * messages) but performs NO network call.
 */
export type EstablishedPeek =
  | { kind: 'cached'; state: OnboardingState }
  | { kind: 'positive_signal'; signal: 'cedar_onboarding_seen' | 'has_messages' }
  | { kind: 'none' };

/**
 * Decide — using ONLY local storage, no fetch — whether the user has clear
 * evidence of established use. Drives index.tsx's cold-start fast-path: an
 * established + locally-valid user is routed straight to chat while the
 * authoritative state is reconciled in the background.
 *
 * Reuses the exact same evidence tiers (and helpers) as
 * resolveOnboardingState's positive-signal cascade, so the two can't drift:
 *   1. cached onboarding state within the 30-day TTL   → { kind: 'cached' }
 *   2. cedar_onboarding_seen flag (per-user or global) → positive_signal
 *   3. local chat-message store has content            → positive_signal
 *   4. none of the above                               → { kind: 'none' }
 *
 * Onboarding state is monotonic, so a cached "completed" snapshot is always
 * safe to act on. Safety (plan §E2): a user behind the email-verification wall
 * can never have produced any of these signals, so a fast-path keyed on them
 * cannot bypass the wall.
 */
export async function peekEstablishedSignal(
  userId: string,
  options?: { messageStoreKey?: string },
): Promise<EstablishedPeek> {
  const messageStoreKey =
    options?.messageStoreKey ?? `${userId}${MESSAGES_KEY_SUFFIX}`;

  const cached = await readCache(userId);
  if (cached) {
    return { kind: 'cached', state: cached.state };
  }

  const perUserFlag = await storage.getItem(`${userId}_cedar_onboarding_seen`);
  const globalFallbackFlag = await storage.getItem('cedar_onboarding_seen_fallback');
  if (perUserFlag === 'true' || globalFallbackFlag === 'true') {
    return { kind: 'positive_signal', signal: 'cedar_onboarding_seen' };
  }

  if (await hasLocalMessages(messageStoreKey)) {
    return { kind: 'positive_signal', signal: 'has_messages' };
  }

  return { kind: 'none' };
}
